/**
 * `YYYY-MM-DD`, one module: every other spot in the client that reads, writes or validates a
 * calendar date (`baseValues.ts`, `valueText.ts`, the audit filter, a form's date field) goes
 * through this rather than its own regex.
 */

import dayjs from 'dayjs';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Strict: the shape AND a real calendar day — `2026-02-30` is refused, not rolled into March. */
export const isIsoDate = (value: string): boolean => {
    if (!ISO_DATE.test(value)) return false;
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};

/** A `Date`'s UTC calendar day, `YYYY-MM-DD`. The caller checks `getTime()` first — this throws on an invalid `Date`. */
export const isoOfDate = (date: Date): string => date.toISOString().slice(0, 10);

/**
 * `YYYY-MM-DD` → the LOCAL midnight `Date` it names — a date picker's value (the audit filter's
 * `from`/`to`), as opposed to `isoOfDate`'s UTC. The caller checks `isIsoDate` first.
 */
export const localDateOfIso = (value: string): Date => dayjs(value).toDate();

/** The inverse of `localDateOfIso`: a local `Date`'s calendar day, `YYYY-MM-DD`. */
export const isoOfLocalDate = (date: Date): string => dayjs(date).format('YYYY-MM-DD');

/** The last millisecond of `iso`'s LOCAL calendar day — a date filter's inclusive `to`. The caller checks `isIsoDate` first. */
export const endOfLocalDay = (iso: string): Date => dayjs(iso).endOf('day').toDate();
